(() => {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const setup = (root = document) => {
    root.querySelectorAll('video[data-toasty-loop]:not([data-loop-ready])').forEach(video => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tt12-moments__toggle';
      button.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path class="tt12-moments__play" d="m9 5 11 7-11 7Z" fill="currentColor"/><path class="tt12-moments__pause" d="M8 5v14M16 5v14" fill="none" stroke="currentColor" stroke-width="3"/></svg>';
      const credit = video.closest('figure')?.querySelector('figcaption')?.textContent.trim();
      let userPaused = false;
      let inView = false;
      let autoBlocked = false;
      let manualPlay = false;
      let pending = false;
      const sync = () => {
        const label = video.paused ? video.dataset.playLabel : video.dataset.pauseLabel;
        button.dataset.paused = String(video.paused);
        button.setAttribute('aria-label', `${label || (video.paused ? 'Play video' : 'Pause video')}${credit ? ` — ${credit}` : ''}`);
      };
      const pause = () => { manualPlay = false; video.pause(); sync(); };
      const shouldAutoPlay = () => inView && !document.hidden && !reducedMotion.matches && !userPaused && !autoBlocked;
      const play = (manual = false) => {
        if (pending) return;
        pending = true;
        manualPlay = manual;
        video.muted = true;
        // Called directly from the button click for browsers requiring a user gesture.
        video.play().then(() => {
          if (document.hidden || (!manualPlay && !shouldAutoPlay()) || userPaused) pause();
        }).catch(error => {
          // Keep the poster/frame and a working manual play button when autoplay is denied.
          autoBlocked = error.name !== 'AbortError';
          manualPlay = false;
        }).finally(() => { pending = false; sync(); });
      };
      const reconcile = () => {
        if (!inView || document.hidden) pause();
        else if (shouldAutoPlay() && video.paused) play();
      };
      button.addEventListener('click', () => {
        if (!video.paused || pending) { userPaused = true; pause(); }
        else { userPaused = false; autoBlocked = false; play(true); }
      });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      video.addEventListener('error', () => {
        // Native controls remain a last-resort retry/open affordance on an actual media error.
        pause();
        video.controls = true;
        button.hidden = true;
      });
      video.addEventListener('loadeddata', () => { video.controls = false; button.hidden = false; sync(); });
      sync();
      video.after(button);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      // Enhance only after the accessible replacement is ready; no-JS keeps native controls.
      video.controls = false;
      video.dataset.loopReady = 'true';
      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => { inView = entry.isIntersecting && entry.intersectionRatio >= 0.35; reconcile(); });
        }, { threshold: [0, 0.35] });
        observer.observe(video);
      }
      const onMotionChange = () => {
        if (reducedMotion.matches) pause();
        else reconcile();
      };
      reducedMotion.addEventListener('change', onMotionChange);
      document.addEventListener('visibilitychange', reconcile);
      const section = video.closest('.shopify-section');
      if (section) section.addEventListener('shopify:section:unload', () => {
        pause();
        observer?.disconnect();
        reducedMotion.removeEventListener('change', onMotionChange);
        document.removeEventListener('visibilitychange', reconcile);
      }, { once: true });
    });
  };
  setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
})();

(() => {
  const setupRails = (root = document) => root.querySelectorAll('.tt12-moments:not([data-rail-ready])').forEach(section => {
    const rail = section.querySelector('.tt12-moments__grid');
    const nav = section.querySelector('.tt12-moments__navigation');
    if (!rail || !nav) return;
    const cards = [...rail.children];
    if (cards.length < 2) return;
    section.dataset.railReady = 'true';
    const socials = section.querySelector('.tt12-moments__socials');
    const header = section.querySelector('.tt12-moments__header');
    const mobileLayout = matchMedia('(max-width: 749px)');
    const placeSocials = () => {
      const parent = mobileLayout.matches ? section : header;
      if (!socials || !parent || socials.parentElement === parent) return;
      const focusedLink = socials.contains(document.activeElement) ? document.activeElement : null;
      parent.appendChild(socials);
      focusedLink?.focus({ preventScroll: true });
    };
    placeSocials();
    mobileLayout.addEventListener('change', placeSocials);
    const previous = nav.querySelector('[data-rail-prev]');
    const next = nav.querySelector('[data-rail-next]');
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let focused = false, dragging = false, navigating = false, inView = false;
    let gestureTimer = 0, navigationFrame = 0;
    let frame = 0, lastTime = 0, x = 0, stride = 0, disposed = false;
    const canMove = () => !disposed && !motion.matches && !dragging && !navigating && !focused && inView && !document.hidden && stride > 0;
    const update = () => {
      previous.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
    };
    const moveCard = (card, before) => {
      if (rail.moveBefore) rail.moveBefore(card, before);
      else {
        const playing = [...card.querySelectorAll('video')].filter(video => !video.paused);
        rail.insertBefore(card, before);
        playing.forEach(video => video.play().catch(() => {}));
      }
    };
    const tick = time => {
      frame = 0;
      if (!canMove()) { lastTime = 0; return; }
      const delta = lastTime ? Math.min(time - lastTime, 50) : 0;
      lastTime = time;
      x += delta * 0.18;
      if (x >= stride) {
        moveCard(rail.firstElementChild, null);
        x -= stride;
      }
      rail.scrollLeft = x;
      update();
      frame = requestAnimationFrame(tick);
    };
    const reconcile = () => {
      cancelAnimationFrame(frame); frame = 0; lastTime = 0; x = rail.scrollLeft;
      update();
      if (canMove()) frame = requestAnimationFrame(tick);
    };
    const go = left => {
      cancelAnimationFrame(navigationFrame);
      navigating = true;
      reconcile();
      const start = rail.scrollLeft;
      const target = Math.max(0, Math.min(rail.scrollWidth - rail.clientWidth, left));
      if (motion.matches) {
        rail.scrollLeft = target;
        navigating = false;
        reconcile();
        return;
      }
      let started;
      const advance = time => {
        started ??= time;
        const progress = Math.min(1, (time - started) / 180);
        rail.scrollLeft = start + (target - start) * (1 - Math.pow(1 - progress, 3));
        update();
        if (progress < 1) navigationFrame = requestAnimationFrame(advance);
        else { navigating = false; navigationFrame = 0; reconcile(); }
      };
      navigationFrame = requestAnimationFrame(advance);
    };
    previous.addEventListener('click', () => go(rail.scrollLeft - stride));
    next.addEventListener('click', () => go(rail.scrollLeft + stride));
    rail.addEventListener('keydown', event => {
      if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      go(event.key === 'Home' ? 0 : event.key === 'End' ? rail.scrollWidth : rail.scrollLeft + (event.key === 'ArrowRight' ? stride : -stride));
    });
    rail.addEventListener('focusin', event => { focused = event.target.matches(':focus-visible'); reconcile(); });
    rail.addEventListener('focusout', event => { focused = rail.contains(event.relatedTarget) && event.relatedTarget.matches(':focus-visible'); reconcile(); });
    const holdGesture = () => {
      clearTimeout(gestureTimer); cancelAnimationFrame(navigationFrame);
      navigating = false; dragging = true; focused = false; reconcile();
    };
    const releaseGesture = () => {
      if (!dragging) return;
      clearTimeout(gestureTimer);
      gestureTimer = setTimeout(() => { dragging = false; reconcile(); }, 200);
    };
    rail.addEventListener('pointerdown', holdGesture, { passive: true });
    window.addEventListener('pointerup', releaseGesture, { passive: true });
    window.addEventListener('pointercancel', releaseGesture, { passive: true });
    rail.addEventListener('wheel', () => { holdGesture(); releaseGesture(); }, { passive: true });
    rail.addEventListener('scroll', update, { passive: true });
    const measure = () => {
      stride = rail.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap);
      nav.hidden = rail.scrollWidth <= rail.clientWidth + 2;
      if (nav.hidden) stride = 0;
      reconcile();
    };
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    const visibility = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; reconcile(); });
    visibility.observe(rail);
    document.addEventListener('visibilitychange', reconcile);
    motion.addEventListener('change', reconcile);
    measure();
    section.closest('.shopify-section')?.addEventListener('shopify:section:unload', () => {
      mobileLayout.removeEventListener('change', placeSocials);
      disposed = true; observer.disconnect(); visibility.disconnect(); cancelAnimationFrame(frame); cancelAnimationFrame(navigationFrame); clearTimeout(gestureTimer);
      window.removeEventListener('pointerup', releaseGesture); window.removeEventListener('pointercancel', releaseGesture);
      document.removeEventListener('visibilitychange', reconcile); motion.removeEventListener('change', reconcile);
    }, { once: true });
  });
  setupRails();
  document.addEventListener('shopify:section:load', event => setupRails(event.target));
})();
